import { RcFile } from "antd/es/upload";

import {
  AVERBADORAS,
  CONTRACT_TYPE,
  DOCUMENT_TYPE,
  EDUCATION_LEVEL,
  KINSHIP_LEVEL,
  MARGIN_TYPE,
  PLANS_TYPE,
  PRODUCT_TYPE,
} from "@utils/enums";

import { ICustomApiOptions } from "./base/useApi";
import { useApiBff } from "./base/useApiBff";
import { useApiOriginacao } from "./base/useApiOriginacao";
import { IAnexoItem } from "./formalization";

export type IApiCriarClienteData = {
  dt_nascimento?: string;
  nu_cpf: string;
  numero_telefone: string;
  tipo_produto?: number;
  nu_beneficio?: number;
  nome_cliente: string;
  convenio_id?: number;
  margem?: number | null;
  numero_matricula?: string;
  numero_beneficio?: string;
  folha?: string;
  folha_compra?: string;
  folha_saque?: string;
  verba?: string;
  verba_compra?: string;
  verba_saque?: string;
  tipo_margem?: MARGIN_TYPE | null;
  tipo_vinculo?: number;
  instituidor?: string;
  orgao?: string;
  classificacao_siape?: string;
  convenio_siape?: string;
  margem_compra?: number | null;
  margem_saque?: number | null;
  escolaridade?: EDUCATION_LEVEL | null;
  ocupacao?: string;
  reoffer_contract_token?: string;
  rogado?: {
    nome?: string;
    telefone?: string;
    cpf?: string;
    grau_parentesco?: KINSHIP_LEVEL | null;
    data_nascimento?: string;
    documento_tipo?: DOCUMENT_TYPE | null;
  };
  representante_legal?: {
    nome?: string;
    telefone?: string;
    cpf?: string;
    grau_parentesco?: KINSHIP_LEVEL;
    data_nascimento?: string;
    documento_tipo?: DOCUMENT_TYPE;
  };
};

export type IApiAtualizarClienteData = {
  cd_cliente_parceiro?: string;
  cd_familiar_unico?: string;
  cliente_id: number;
  conjuge_cpf?: string;
  conjuge_data_nascimento?: string;
  conjuge_nome?: string;
  convenio?: number;
  dados_bancarios?: Array<{
    id?: number | null;
    conta_agencia?: string | null;
    conta_banco?: string | null;
    conta_digito?: string | null;
    conta_numero?: string | null;
    conta_tipo?: number | null;
  }>;
  documento_data_emissao?: string | null;
  documento_numero?: string | null;
  documento_orgao_emissor?: string | null;
  documento_tipo?: number | null;
  documento_uf?: number | null;
  documentacao_cliente?: {
    tipo_documento?: number | null;
    numero_documento?: string | null;
    data_emissao?: string | null;
    orgao_emissor?: string | null;
    uf_emissor?: number | null;
  };
  dt_nascimento: string;
  email?: string | null;
  endereco_bairro?: string;
  endereco_cep?: string;
  endereco_cidade?: string;
  endereco_complemento?: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_residencial_tipo?: number;
  endereco_uf?: string;
  estado_civil?: string | null;
  folha?: number | string | null;
  margem_atual?: number;
  nacionalidade?: string;
  naturalidade?: string;
  nome_cliente: string;
  nome_mae?: string | null;
  nome_pai?: string;
  nu_cpf: string;
  renda?: number | null;
  sexo?: string | null;
  telefone_celular: string;
  telefone_residencial?: number;
  tipo_cliente?: number;
  tipo_logradouro?: string;
  numero_matricula?: string | null;
  invalidez?: boolean | null;
  produtos?: Array<PRODUCT_TYPE>;
};

type IApiCriarEnvelopeData = {
  inicio_digitacao: number;
};

export type IApiCriarContratoData = {
  numero_cpf?: string | null;
  tipo_produto?: PRODUCT_TYPE | null;
  tipo_contrato?: CONTRACT_TYPE | null;
  token_envelope?: string | null;
  token_contrato?: string | null;
  fl_seguro?: boolean | null;
  folha?: string | null;
  telefone_celular?: string | null;
  contrato_cross_sell?: boolean | null;
  convenio?: number | null;
  dt_desembolso?: string | null;
  dt_nascimento?: string | null;
  dt_vencimento_primeira_parcela?: string | null;
  dt_vencimento_ultima_parcela?: string | null;
  margem_atual?: number | null;
  numero_matricula?: string | null;
  numero_beneficio?: string | null;
  contrato_digitacao_manual?: boolean | null;
  senha_servidor?: string | null;
  cet_mes?: number | null;
  cet_ano?: number | null;
  limite_pre_aprovado?: number | null;
  possui_saque?: boolean | null;
  qt_parcelas_total?: number | null;
  saque_parcelado?: boolean | null;
  taxa?: number | null;
  taxa_efetiva_mes?: number | null;
  taxa_efetiva_ano?: number | null;
  valor_disponivel_saque?: number | null;
  valor_financiado?: number | null;
  vr_contrato?: number | null;
  vr_iof?: number | null;
  vr_iof_adicional?: number | null;
  vr_iof_total?: number | null;
  vr_liberado_cliente?: number | null;
  vr_parcela?: number | null;
  vr_parcela_maxima?: number | null;
  vr_parcela_minima?: number | null;
  vr_seguro?: number | null;
  valor_saque?: number | null;
  vencimento_fatura?: string | null;
  verba?: string | null;
  anexo_cnh?: RcFile;

  // Cartão
  averbadora?: number | null;
  id_cliente_cartao?: number | null;
  tipo_margem?: MARGIN_TYPE | null;
  verba_compra?: string | null;
  verba_saque?: string | null;
  folha_compra?: string | null;
  folha_saque?: string | null;
  cartoes?: Array<CardRawData> | null;

  id_cliente?: number | null;
  id_rogado?: string | null;
  escolaridade?: EDUCATION_LEVEL | null;
  identificador_usuario?: string | null;
  documentos_reaproveitados_ids?: number[];
  testemunhas?: Array<
    | {
        cpf?: string | null;
        nome?: string | null;
        data_nascimento?: string | null;
        documento_tipo?: DOCUMENT_TYPE | null;
      }
    | null
    | undefined
  >;

  aceite_nmp?: boolean;

  dado_bancario_id?: number | null;
};

type BaseCriarContratoBffPayload = {
  metricas: {
    inicio_digitacao: number;
    fim_digitacao: number;
  };
  numero_cpf: string;
  aceite_nmp: boolean;
  dado_bancario_id: number;
  id_rogado: string | null;
  testemunhas: Array<{
    nome: string;
    cpf: string;
    data_nascimento: string; // datetime
    documento_tipo: DOCUMENT_TYPE;
  }> | null;
};

export type IApiNovoCriarContratoData = BaseCriarContratoBffPayload & {
  convenio: number | null;
  averbadora: number | null;
  contrato_digitacao_manual: boolean;
  cartoes: Array<{
    margem_atual: number;
    margem_compra: number;
    margem_saque: number;
    folha: string | null;
    folha_compra: string | null;
    folha_saque: string | null;
    verba: string | null;
    verba_compra: string | null;
    verba_saque: string | null;
    numero_matricula: string | null;
    tipo_produto: number;
    tipo_margem: MARGIN_TYPE;
    ocupacao: string | null;
    convenio_siape: string | null;
    classificacao_siape: string | null;
    instituidor: string | null;
    senha_servidor: string | null;
    tipo_contrato: CONTRACT_TYPE;
    id_cliente_cartao: number;
    tipo_saque: number | null;
    limite_pre_aprovado: number;
    valor_saque: number;
    valor_disponivel_saque: number;
    taxa: number;
    taxa_efetiva_ano: number;
    cet_mes: number;
    cet_ano: number;
    vr_iof_total: number;
    valor_financiado: number;
    dt_vencimento_fatura: string | null; // datetime yyyy-mm-dd
    qtd_parcela_saque_parcelado: number;
    valor_parcela: number;
    valor_total_a_pagar: number;
    possui_saque: boolean;
    saque_parcelado: boolean;
    limite_pre_aprovado_compra: number;
    limite_pre_aprovado_saque: number;
    vr_iof_adicional: number;
    vr_iof: number;
    vencimento_fatura: string | null;
    seguro: boolean;
    plano: Array<{
      id: string;
      valor: number;
      obrigatorio: boolean;
      tipo: PLANS_TYPE;
    }>;
    bank_data_id: number;
    taxa_especial: boolean;
    id_taxa_especial: number | null;
    id_operacao_calculo?: string | null;
  }>;
  id_cliente: number | null;
};

export type IApiNovoCriarContratoRefinData = IApiNovoCriarContratoData;

export type IApiNovoCriarContratoSaqueData = BaseCriarContratoBffPayload & {
  saques: Array<{
    possui_saque: boolean;
    saque_parcelado: boolean;
    valor_saque: number;
    vencimento: string; // datetime
    limite_disponivel_saque: number;
    limite_pre_aprovado: number;
    id_cliente_cartao: number;
    valor_lancado_fatura: number;
    valor_iof_total: number;
    taxa_produto: number;
    taxa_anual_produto: number;
    cet_mensal: number;
    cet_anual: number;
    vr_iof_adicional: number;
    valor_iof_diario_em_dinheiro: number;
    qtd_parcela_saque_parcelado: number | null;
    somatorio_parcelas: number | null;
    valor_parcela: number | null;
    id_taxa_especial?: number | null;
    taxa_especial: boolean;

    plano?: Array<{
      id: number;
      valor: number;
      obrigatorio: boolean; // TODO: Check if used
      tipo?: PLANS_TYPE;
    }> | null;
    seguro?: boolean | null;
  }>;
};

export type IApiNovoCriarContratoCompraClienteData =
  BaseCriarContratoBffPayload & {
    numero_cpf: string;
    convenio: number | null;
    dado_bancario_id: number;
    aceite_nmp: boolean;
    contrato_digitacao_manual?: boolean | null;
    // cartoes: Array<{
    //   tipo_produto: PRODUCT_TYPE;
    //   tipo_contrato: CONTRACT_TYPE;
    //   id_cliente_cartao: number;
    //   tipo_margem: MARGIN_TYPE;
    //   folha_compra: string;
    //   boletos: Array<number>;
    //   taxa: number;
    //   taxa_efetiva_ano: number;
    //   cet_mes: number;
    //   cet_ano: number;
    //   valor_disponivel_saque: number;
    //   banco_originador: number;
    //   valor_titulo: number;
    //   seguro: boolean;
    //   numero_matricula: string;
    // }>;
    cartoes?: Array<
      DebtRawData & { banco_originador?: string; valor_titulo?: number }
    > | null;
  };

type IApiBuscarCEPData = {
  cep: string;
};

type IApiEnvioDocumentosData = FormData /* {
  tipo_anexo?: number;
  arquivo: any;
  nome_anexo: string;
  anexo_extensao: string;
  token_contrato: string;
  cliente: number;
  contrato_id: number;
  contratos_id: number;
} */;

/**
 * {
 *  image-validation: string;
 *  file-type: string;
 *  contract_id: number;
 * }
 * */
export type IApiValidaDocumentosData = {
  "image-validation": string;
  "file-type": string;
  contract_id: number;
};

type IApiResetPasswordByCPFData = {
  cpf: string;
};

export type IApiCriarClienteResponse = {
  dt_nascimento: string;
  id: number;
  nu_cpf: string;
  cliente_cartao_beneficio_id?: number;
  rogado: {
    id: string;
    nome: string;
    telefone: string;
    cpf: string;
    grau_parentesco: KINSHIP_LEVEL;
    data_nascimento: string;
  };
  representante_legal?: {
    id: string;
  };
};

export type IApiAtualizarClienteResponse = {
  msg: string;
  data?: {
    dado_bancario_id?: number;
  };
};

export type IApiCriarEnvelopeResponse = {
  token_envelope: string;
};

type CriarContratoWitness = {
  id: string;
  cliente_id: string;
  nome: string;
  cpf: string;
  data_nascimento: string;
  documento_tipo: string;
};

type CriarContratoBaseResponse = {
  chave_proposta: string | null;
  cliente: {
    dt_nascimento: string;
    id: number;
    nome_cliente: string;
    nu_cpf: string;
  };
  id: number;
  necessita_assinatura_fisica?: boolean;
  testemunhas: CriarContratoWitness[];
  token_contrato: string;
  vulnerabilidade?: string;
};

type CriarContratoCardResponse = CriarContratoBaseResponse[];

export type IApiCriarContratoResponse =
  | CriarContratoBaseResponse
  | CriarContratoCardResponse;

export type BaseCriarContratoBffResponse = {
  token_envelope: string;
  contratos: Array<{
    cliente: {
      id: number;
      nu_cpf: string;
      nome_cliente: string;
      dt_nascimento: string;
    };
    token_contrato: string;
    id: number;
    testemunhas: CriarContratoWitness[];
    vulnerabilidade?: string;
    necessita_assinatura_fisica: boolean;
  }>;
  motivo_url_indisponivel?: string;
  url_formalizacao_curta: string;
  url_formalizacao_rogado?: string;
  anexos?: IAnexoItem[];
};

export type IApiNovoCriarContratoResponse = BaseCriarContratoBffResponse;
export type IApiNovoCriarContratoRefinResponse = BaseCriarContratoBffResponse;
export type IApiNovoCriarContratoSaqueResponse = BaseCriarContratoBffResponse;
export type IApiNovoCriarContratoCompraClienteResponse =
  BaseCriarContratoBffResponse;

export type IApiBancosResponse = Array<{
  codigo: string;
  id: number;
  nome_: string;
}>;

export type IApiBancosData = {
  cd_produto: string;
  cd_convenio?: string;
  produtos?: Array<number>;
};

export type IApiTaxaResponse = Array<{
  ativo: boolean;
  id: number;
  taxa: string;
  vr_max: string;
  vr_min: string;
  transbordo?: boolean;
}>;

export type IApiTiposdeContaBancariaResponse = Array<{
  id: number;
  tipo_conta: string;
}>;

export type IApiListarDocumentosResponse = Array<{
  id: DOCUMENT_TYPE;
  documento: string;
}>;

export type IApiListarUfsResponse = Array<{
  estado: string;
  id: number;
}>;

export type IApiListarSexosResponse = Array<{
  id: number;
  sexo: string;
}>;

export type IApiTermosUsoResponse = {
  count: number;
  next: number | null;
  previous: number | null;
  results: Array<{
    termos_de_uso: string;
    politica_privacidade: string;
  }>;
};

export type IApiBuscarCEPResponse =
  | { erro: "true" }
  | {
      bairro: string;
      cep: string;
      localidade: string;
      complemento: string;
      logradouro: string;
      unidade?: string;
      uf: string;
      estado?: string;
      regiao?: string;
      ibge?: string;
      gia?: string;
      ddd?: string;
      siafi?: string;
    };

type IApiEnvioDocumentosResponse = {
  anexado_em: string;
  anexo_extensao: string;
  anexo_url: string;
  contrato: number;
  nome_anexo: string;
  tipo_anexo: number;
};

export type IApiParametroProdutoResponse = {
  id: number;
  nome: string;
  cd_produto: string;
  documento_pessoal: boolean;
  comprovante_residencia: boolean;
  contracheque: boolean;
  ativo: boolean;
};

export type IApiValidaDocumentosResponse = {
  message: string;
  status: boolean;
  signature: string;
};

export type IApiParametroProdutoData = {
  tipo_produto: PRODUCT_TYPE;
  convenio?: number;
  created_by: number;
  cliente_id?: number;
  endereco: {
    cep: boolean;
    logradouro: boolean;
    numero: boolean;
    bairro: boolean;
    cidade: boolean;
    uf: boolean;
    complemento: boolean;
  };
};

type IApiValidarTelefoneData = {
  telefone_celular: string;
  tipo_produto: PRODUCT_TYPE;
};

export type AvailableOfferProduct<T = { tipo_produto: PRODUCT_TYPE }> =
  T extends {
    tipo_produto:
      | PRODUCT_TYPE.CARTAO_BENEFICIO
      | PRODUCT_TYPE.CARTAO_CONSIGNADO;
  }
    ? {
        margem_atual: number;
        convenio: number;
        folha: number;
        numero_matricula: string;
        averbadora: AVERBADORAS;
        necessita_assinatura_fisica: boolean;
        idade_minima_assinatura: number;
        tipo_margem: MARGIN_TYPE;
        tipo_produto:
          | PRODUCT_TYPE.CARTAO_BENEFICIO
          | PRODUCT_TYPE.CARTAO_CONSIGNADO;
      }
    : { tipo_produto: PRODUCT_TYPE };

export type IApiProductParamsResponse = Array<{
  tipoProduto: PRODUCT_TYPE;
  permite_representante_legal: boolean;
  idade_minima: number;
  idade_maxima: number;
  idade_minima_cliente_com_representante_legal: number;
  idade_maxima_cliente_com_representante_legal: number;
  idade_minima_do_representante_legal: number;
  idade_maxima_do_representante_legal: number;
}>;

type IApiUpdateLegalRepresentativeData = {
  documento_tipo_input: DOCUMENT_TYPE;
  documento_numero: string;
  documento_uf: number;
  documento_orgao_emissor: string;
  documento_data_emissao: string;
  email: string;
};

export type IApiBureauCadastralResponse =
  | {
      permite_digitacao: true;
      mensagem: string;
      grupo_dados_pessoais: {
        data_nascimento?: string;
        endereco?: string[];
        enderecos?: string[];
        endereco_json?: Array<{
          cep: number;
          cidade: string;
          municipio: string;
          numero: string;
          rua: string;
          uf: string | null;
          complemento: string | null;
        }>;
        emails?: string[];
        nome_da_mae?: string;
        nome?: string;
      };
    }
  | {
      permite_digitacao: false;
      mensagem: string;
      grupo_dados_pessoais: undefined;
    };

type IApiBureauCadastralData = {
  nu_cpf: string;
  produto: number;
  convenio?: number;
  canal: string;
  corban: number;
  loja: number;
};

type IApiValidateEspecieAndCpfResponse = {
  is_valid: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type IApiFeatureFlagsResponse =
  | Array<{
      name: string;
      is_active?: boolean;
    }>
  | undefined
  | null;

export type IApiResetPasswordByCPFErrorResponse =
  | {
      status: "error";
      message?: string;
      errors?: {
        cpf: string;
      };
    }
  | {
      status: "cooldown";
      message: string;
      retry_after: string; //datetime
    };

export type IApiResetPasswordByCPFResponse = {
  status: "success";
  masked_email: string;
};

export const useApiCriarCliente = () => {
  return useApiOriginacao<IApiCriarClienteResponse, IApiCriarClienteData>({
    method: "POST",
    url: "/criar-cliente/",
  });
};

export const useApiAtualizarCliente = () => {
  return useApiOriginacao<
    IApiAtualizarClienteResponse,
    IApiAtualizarClienteData
  >({
    method: "PATCH",
    url: "/atualizar-cliente/",
    headers: { Accept: "application/json; version=2" },
  });
};

export const useApiValidarTelefone = () => {
  return useApiOriginacao<IApiValidarTelefoneData>({
    method: "POST",
    url: "/validar-telefone/",
  });
};

export const useApiCriarEnvelope = () => {
  return useApiOriginacao<IApiCriarEnvelopeResponse, IApiCriarEnvelopeData>({
    method: "POST",
    url: "/criar-envelope/",
    headers: { Accept: "application/json; version=2" },
  });
};

export const useApiCriarContrato = () => {
  return useApiOriginacao<IApiCriarContratoResponse, IApiCriarContratoData>({
    method: "POST",
    url: "/criar-contrato/",
  });
};

export const useApiBffCriarContratoCartao = () => {
  return useApiBff<IApiNovoCriarContratoResponse, IApiNovoCriarContratoData>({
    method: "POST",
    url: "/criar-contrato/cartao-beneficio/",
  });
};

export const useApiBffCriarContratoSaque = () => {
  return useApiBff<
    IApiNovoCriarContratoSaqueResponse,
    IApiNovoCriarContratoSaqueData
  >({
    method: "POST",
    url: "/criar-contrato/saque-complementar/",
  });
};

export const useApiBffCriarContratoRefin = () => {
  return useApiBff<
    IApiNovoCriarContratoRefinResponse,
    IApiNovoCriarContratoRefinData
  >({
    method: "POST",
    url: "/criar-contrato/refin/",
  });
};

export const useApiBffCriarContratoRepurchase = () => {
  return useApiBff<
    IApiNovoCriarContratoCompraClienteResponse,
    IApiNovoCriarContratoCompraClienteData
  >({
    method: "POST",
    url: "/criar-contrato/compra-cliente/",
  });
};

export const useApiBancos = () => {
  return useApiOriginacao<IApiBancosResponse, IApiBancosData>({
    method: "POST",
    url: "/bancos/",
  });
};

export const useApiTaxa = () => {
  return useApiOriginacao<IApiTaxaResponse>({
    method: "GET",
    url: "/taxa/",
  });
};

export const useApiTiposDeContaBancaria = () => {
  return useApiOriginacao<IApiTiposdeContaBancariaResponse>({
    method: "GET",
    url: "/tipos-de-conta-bancaria/",
  });
};

export const useApiListarDocumentos = () => {
  return useApiOriginacao<IApiListarDocumentosResponse>({
    method: "GET",
    url: "/listar-documentos/",
  });
};

export const useApiListarUfs = () => {
  return useApiOriginacao<IApiListarUfsResponse>({
    method: "GET",
    url: "/listar-ufs/",
  });
};

export const useApiListarSexos = () => {
  return useApiOriginacao<IApiListarSexosResponse>({
    method: "GET",
    url: "/listar-sexos/",
  });
};

export const useApiTermosUso = () => {
  return useApiOriginacao<IApiTermosUsoResponse>({
    method: "GET",
    url: "/termos-de-uso/",
  });
};

export const useApiBuscarCEP = () => {
  return useApiOriginacao<IApiBuscarCEPResponse, IApiBuscarCEPData>({
    method: "POST",
    url: "/buscar-cep/",
  });
};

export const useApiEnvioDocumentos = () => {
  return useApiOriginacao<IApiEnvioDocumentosResponse, IApiEnvioDocumentosData>(
    {
      method: "POST",
      url: "/envio-documentos/",
    },
  );
};

export const useApiParametroProduto = () => {
  return useApiOriginacao<
    IApiParametroProdutoResponse,
    IApiParametroProdutoData
  >({
    method: "POST",
    url: "/contratos/consulta-documentos-produto/",
  });
};

export const useApiProductParams = () => {
  return useApiOriginacao<IApiProductParamsResponse>({
    method: "GET",
  });
};

export const useApiUpdateLegalRepresentative = () => {
  return useApiOriginacao<IApiUpdateLegalRepresentativeData>({
    method: "PATCH",
  });
};

export const useApiBureauCadastral = (config?: ICustomApiOptions) => {
  return useApiOriginacao<IApiBureauCadastralResponse, IApiBureauCadastralData>(
    {
      method: "POST",
      url: "/bureau-cadastral/",
    },
    config,
  );
};

export const useApiValidateEspecieAndCpf = () => {
  return useApiOriginacao<IApiValidateEspecieAndCpfResponse>({
    method: "GET",
  });
};

export const useApiValidaDocumentos = () => {
  return useApiBff<IApiValidaDocumentosResponse, IApiValidaDocumentosData>({
    method: "POST",
    url: "/imagevalidation",
  });
};

export const useApiFeatureFlags = () => {
  return useApiOriginacao<IApiFeatureFlagsResponse>({
    method: "GET",
    url: "/feature-flags/",
  });
};

export const useApiResetPasswordByCpf = () => {
  return useApiOriginacao<
    IApiResetPasswordByCPFResponse,
    IApiResetPasswordByCPFData,
    IApiResetPasswordByCPFErrorResponse
  >({
    method: "POST",
    url: "/auth/password-request-reset-by-cpf/",
  });
};
